import { useParams } from '@tanstack/react-router';
import NotExists from '_/components/common/NotExists';
import { useCollections } from '_/hooks/useCollections';
import { ICollection } from '_/types/collections/collections.types';
import React, { createContext, useContext, ReactNode, useMemo } from 'react';

import { useOrganization } from './OrganizationContext';
import { useProject } from './ProjectContext';

interface CollectionContextValue {
  collections: ICollection[];
  selectedCollection: ICollection | null;
  selectedCollectionSlug: string | null;
  isLoading: boolean;
  notFound: boolean;
  refetch: () => void;
}

const CollectionContext = createContext<CollectionContextValue | undefined>(undefined);

export function CollectionProvider({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as Partial<{
    collectionSlug?: string;
    projectSlug?: string;
  }>;
  const collectionSlug = params.collectionSlug;
  const projectSlug = params.projectSlug;
  const { selectedOrgSlug } = useOrganization();
  const { selectedProject } = useProject();

  const { data: collections = [], isLoading, refetch } = useCollections(selectedProject?.id);

  const selectedCollection = useMemo(() => {
    // Don't try to find collections for special route slugs
    if (!collectionSlug || collectionSlug === '_new') return null;
    return collections.find((collection) => collection.slug === collectionSlug) || null;
  }, [collections, collectionSlug]);

  // Determine if collection is not found: when loading is done, we have a collectionSlug, but no matching collection
  const notFound = !isLoading && !!collectionSlug && collectionSlug !== '_new' && !selectedCollection;

  const value: CollectionContextValue = {
    collections: collections,
    selectedCollection: selectedCollection,
    selectedCollectionSlug: collectionSlug || null,
    isLoading: isLoading,
    notFound: notFound,
    refetch: refetch,
  };

  // If collection not found, render NotExists page
  if (notFound) {
    return (
      <NotExists
        entityType="Collection"
        backUrl={`/orgs/${selectedOrgSlug}/projects/${projectSlug}/collections`}
        backLabel="Back to Collections"
      />
    );
  }

  return <CollectionContext.Provider value={value}>{children}</CollectionContext.Provider>;
}

export function useCollection(): CollectionContextValue {
  const context = useContext(CollectionContext);
  if (context === undefined) {
    throw new Error('useCollection must be used within a CollectionProvider');
  }
  return context;
}

/**
 * Safe version of useCollection that returns null when used outside CollectionProvider.
 * Use this when CollectionContext may not be available.
 */
export function useCollectionSafe(): CollectionContextValue | null {
  const context = useContext(CollectionContext);
  return context === undefined ? null : context;
}
